import React from 'react';
import { FieldTemplateProps } from '@rjsf/utils';

const HorizontalFieldTemplate = ({
    id,
    label,
    help,
    required,
    description,
    errors,
    children,
    displayLabel,
    classNames,
    disabled,
    readonly,
    schema,
    uiSchema,
    onKeyChange,
    onDropPropertyClick
}: FieldTemplateProps) => {
    // Don't render anything for hidden fields
    if (uiSchema?.['ui:widget'] === 'hidden') {
        return <>{children}</>;
    }

    // Full-width fields: the widget spans the whole form row instead of the default
    // 50/50 label/field split. Used by space-hungry widgets (e.g. the collar-
    // registration file table). Flagged via `ui:options.fullWidth` in generateUISchema.
    // The widget owns its own header (label + description + actions) so it can lay them
    // out across the full width — see CustomFileField.
    // WARNING (footgun): in this branch the template renders ONLY the widget — it does
    // NOT render the field label/description. Only flag a field fullWidth if its widget
    // draws its own header; otherwise the field's label silently disappears.
    // NOTE: this fullWidth mechanism will likely be refactored once Vectronic-style
    // integrations are managed through "Source Configurations" rather than through
    // data-provider configurations.
    if ((uiSchema?.['ui:options'] as any)?.fullWidth === true) {
        return (
            <div className={`${classNames} mb-[8px] border-b border-ui-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full`}>
                <div className="w-full">{children}</div>
                {errors}
                {help}
            </div>
        );
    }

    // Boolean fields use the same two-column layout as text fields.
    // Note: rjsf sets displayLabel=false for booleans by default (expecting the widget to render it),
    // so we render label unconditionally here instead of using displayLabel.
    if (schema.type === 'boolean') {
        return (
            <div className={`${classNames} mb-[8px] border-b border-ui-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full`}>
                <div className="flex flex-row items-stretch justify-between w-full gap-6">
                    <div className="w-1/2 min-w-0 flex flex-col">
                        <span className="mr-1">{label}</span>
                        {description}
                    </div>
                    <div className="w-1/2 min-w-0 flex items-center justify-end p-2">
                        {children}
                    </div>
                </div>
                {errors}
                {help}
            </div>
        );
    }

    // For nested objects/arrays, use vertical layout
    if (schema.type === 'object' || schema.type === 'array') {
        return (
            <div className={`${classNames}`}>
                {displayLabel && label && <div>{label}</div>}
                <div>{children}</div>
                {errors}
                {help}
            </div>
        );
    }

    return (
        <div className={`${classNames} mb-[8px] border-b border-ui-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full`}>
            {/* Squeeze: keep the label and field columns side-by-side at every width and
                let them shrink together. `min-w-0` defeats flexbox's default min-width:auto
                so the full-width input compresses instead of overflowing a narrow card.
                NOTE: responsive breakpoints across these forms are viewport-based. The real
                constraint is the card/column width — if a form is ever rendered in a narrow
                column (e.g. a side panel), switch these toggles to CSS container queries. */}
            <div className="flex flex-row items-start justify-between w-full gap-6">
                <div className="w-1/2 min-w-0 flex flex-col">
                    <span className="mr-1">{displayLabel && label}</span>
                    {description}
                </div>
                <div className="w-1/2 min-w-0">
                    <div className="p-2">{children}</div>
                    {errors}
                    {help}
                </div>
            </div>
        </div>
    );
};

export default HorizontalFieldTemplate;
